import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';
import { chainColors } from '@/data/blockchainMockData';

const EdgeCreatorModal = () => {
  const { language } = useLanguage();
  const { createUserEdge, connectSource, toggleConnectMode } = useGraphStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [targetNode, setTargetNode] = useState(null);
  const [edgeData, setEdgeData] = useState({
    amount: 1,
    type: 'outflow',
    label: '',
  });
  
  const t = {
    createEdge: language === 'de' ? 'Verbindung erstellen' : 'Create Connection',
    from: language === 'de' ? 'Von' : 'From',
    to: language === 'de' ? 'Nach' : 'To',
    amount: language === 'de' ? 'Betrag' : 'Amount',
    direction: language === 'de' ? 'Richtung' : 'Direction',
    label: language === 'de' ? 'Bezeichnung' : 'Label',
    inflow: language === 'de' ? 'Eingang' : 'Inflow',
    outflow: language === 'de' ? 'Ausgang' : 'Outflow',
    cancel: language === 'de' ? 'Abbrechen' : 'Cancel',
    create: language === 'de' ? 'Erstellen' : 'Create',
    optional: language === 'de' ? 'Optional' : 'Optional',
  };
  
  // Listen for edge creation events
  useEffect(() => {
    const handleCreateEdge = (event) => {
      setTargetNode(event.detail.target);
      setIsOpen(true);
    };
    
    window.addEventListener('createEdge', handleCreateEdge);
    return () => window.removeEventListener('createEdge', handleCreateEdge);
  }, []);
  
  const handleCreate = () => {
    createUserEdge(targetNode, edgeData);
    setIsOpen(false);
    setEdgeData({ amount: 1, type: 'outflow', label: '' });
    setTargetNode(null);
  };
  
  const handleCancel = () => {
    setIsOpen(false);
    setEdgeData({ amount: 1, type: 'outflow', label: '' });
    setTargetNode(null);
    toggleConnectMode();
  };
  
  if (!connectSource || !targetNode) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <i className="fa-solid fa-link text-primary"></i>
            {t.createEdge}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* From Node */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: chainColors[connectSource.chain] || connectSource.color }}
            />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">{t.from}</p>
              <p className="text-sm font-medium">{connectSource.label}</p>
              <p className="text-xs font-mono text-muted-foreground">
                {connectSource.address.slice(0, 12)}...
              </p>
            </div>
          </div>
          
          {/* Arrow */}
          <div className="flex justify-center">
            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1 }}
            >
              <i className={cn(
                "fa-solid fa-arrow-down text-xl",
                edgeData.type === 'inflow' ? 'text-success rotate-180' : 'text-destructive'
              )}></i>
            </motion.div>
          </div>
          
          {/* To Node */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: chainColors[targetNode.chain] || targetNode.color }}
            />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">{t.to}</p>
              <p className="text-sm font-medium">{targetNode.label}</p>
              <p className="text-xs font-mono text-muted-foreground">
                {targetNode.address.slice(0, 12)}...
              </p>
            </div>
          </div>
          
          {/* Edge Properties */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">{t.amount}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={edgeData.amount}
                onChange={(e) => setEdgeData({ ...edgeData, amount: parseFloat(e.target.value) || 0 })}
                className="h-9"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">{t.direction}</Label>
              <Select
                value={edgeData.type}
                onValueChange={(value) => setEdgeData({ ...edgeData, type: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outflow">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-destructive"></span>
                      {t.outflow}
                    </span>
                  </SelectItem>
                  <SelectItem value="inflow">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-success"></span>
                      {t.inflow}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs">{t.label} <span className="text-muted-foreground">({t.optional})</span></Label>
            <Input
              type="text"
              placeholder="e.g., Payment, Transfer..."
              value={edgeData.label}
              onChange={(e) => setEdgeData({ ...edgeData, label: e.target.value })}
              className="h-9"
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel}>
            {t.cancel}
          </Button>
          <Button onClick={handleCreate}>
            <i className="fa-solid fa-plus mr-2"></i>
            {t.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EdgeCreatorModal;
